import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useConsumerLocation, type ConsumerLocationOptions } from './use-consumer-location';

type ConsumerLocationApi = ReturnType<typeof useConsumerLocation>;

export interface DiscoveryLocationApi extends Pick<ConsumerLocationApi, 'location' | 'status'> {
  /** Cidade escolhida à mão: substitui GPS/perfil na sessão e, nos resultados, entra na URL. */
  chooseCity: (cityId: string) => boolean;
  /** Pedido de GPS — SEMPRE por gesto do utilizador (C4), nunca no mount. */
  requestDevice: () => Promise<boolean>;
  pickerOpen: boolean;
  openPicker: () => void;
  closePicker: () => void;
}

const DiscoveryLocationContext = createContext<DiscoveryLocationApi | null>(null);

const RESULTS_PATH = '/home/busca';

/**
 * Origem única da descoberta, partilhada pelo cabeçalho (controlo «Perto de») e pelas páginas.
 *
 * O `cityId` da URL prevalece sobre a sessão (C2): um link partilhado abre na cidade de quem o
 * enviou. A origem por GPS nunca vai para a URL — fica em memória/sessão, arredondada a 3 casas.
 */
export function DiscoveryLocationProvider({ children, ...options }: Omit<ConsumerLocationOptions, 'cityId'> & { children: ReactNode }) {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(routerLocation.search);
  const cityId = params.get('city') ?? undefined;
  const api = useConsumerLocation({ ...options, cityId });
  const [pickerOpen, setPickerOpen] = useState(false);
  const { selectCity, requestDeviceLocation } = api;
  const onResults = routerLocation.pathname.replace(/\/+$/, '') === RESULTS_PATH;

  const syncUrlCity = useCallback(
    (next: string | null) => {
      const search = new URLSearchParams(routerLocation.search);
      if ((search.get('city') ?? null) === next) return;
      if (next) search.set('city', next);
      else search.delete('city');
      navigate({ pathname: routerLocation.pathname, search: search.toString() ? `?${search}` : '' });
    },
    [navigate, routerLocation.pathname, routerLocation.search],
  );

  const chooseCity = useCallback(
    (id: string) => {
      const ok = selectCity(id);
      if (ok && (onResults || cityId)) syncUrlCity(id);
      return ok;
    },
    [cityId, onResults, selectCity, syncUrlCity],
  );

  const requestDevice = useCallback(async () => {
    const ok = await requestDeviceLocation();
    // GPS substitui a cidade manual: a cidade sai da URL para não reaparecer num reload.
    if (ok && cityId) syncUrlCity(null);
    return ok;
  }, [cityId, requestDeviceLocation, syncUrlCity]);

  const value = useMemo<DiscoveryLocationApi>(
    () => ({
      location: api.location,
      status: api.status,
      chooseCity,
      requestDevice,
      pickerOpen,
      openPicker: () => setPickerOpen(true),
      closePicker: () => setPickerOpen(false),
    }),
    [api.location, api.status, chooseCity, requestDevice, pickerOpen],
  );

  return <DiscoveryLocationContext.Provider value={value}>{children}</DiscoveryLocationContext.Provider>;
}

export function useDiscoveryLocation(): DiscoveryLocationApi {
  const value = useContext(DiscoveryLocationContext);
  if (!value) throw new Error('useDiscoveryLocation fora de DiscoveryLocationProvider');
  return value;
}
